import { ActionContext } from "vuex";
import vehicleService from "@/services/vehicleService";
import {
  VehicleListParams,
  VehicleListItemDto,
  VehicleResponseDto,
  CreateVehicleDto,
  UpdateVehicleDto,
} from "@/models/vehicle.model";
import { ErrorCode } from "@/Enums/ErrorCodes";
import { extractErrorCode } from "@/utils/errorUtils";

interface State {
  vehicles: VehicleListItemDto[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  currentVehicle: VehicleResponseDto | null;
}

type Ctx = ActionContext<State, any>;

const state: State = {
  vehicles: [],
  total: 0,
  page: 1,
  pageSize: 10,
  totalPages: 0,
  currentVehicle: null,
};

const mutations = {
  setVehicles(
    state: State,
    payload: {
      items: VehicleListItemDto[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }
  ) {
    state.vehicles = payload.items;
    state.total = payload.total;
    state.page = payload.page;
    state.pageSize = payload.pageSize;
    state.totalPages = payload.totalPages;
  },
  setCurrentVehicle(state: State, payload: VehicleResponseDto | null) {
    state.currentVehicle = payload;
  },
};

const actions = {
  // ==================== VEHICLES ====================

  async loadVehicles({ commit, dispatch }: Ctx, params: VehicleListParams = {}) {
    try {
      const response = await vehicleService.listVehicles(params);
      commit("setVehicles", response);
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchVehicle({ commit, dispatch }: Ctx, dealerVehicleId: string) {
    try {
      const vehicle = await vehicleService.getVehicleById(dealerVehicleId);
      commit("setCurrentVehicle", vehicle);
      return vehicle;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      commit("setCurrentVehicle", null);
      throw error;
    }
  },

  async searchVehicleByVin({ dispatch }: Ctx, vin: string) {
    try {
      const spec = await vehicleService.searchByVin(vin);
      return spec;
    } catch (error) {
      const errorCode = extractErrorCode(error);
      // Solo mostrar error si NO es "not found" (el form maneja ese caso)
      if (errorCode !== ErrorCode.VEHICLE_NOT_FOUND) {
        await dispatch("notificator/apiError", error, { root: true });
      }
      throw error;
    }
  },

  async refreshVehicleFromApi(
    { dispatch }: Ctx,
    payload: string | { vin: string; silent?: boolean }
  ) {
    try {
      const vin = typeof payload === "string" ? payload : payload.vin;
      const silent = typeof payload === "string" ? false : !!payload.silent;
      const spec = await vehicleService.refreshFromApi(vin);
      if (!silent) {
        dispatch("notificator/success", "save-success", { root: true });
      }
      return spec;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async createVehicle(
    { dispatch }: Ctx,
    payload: CreateVehicleDto
  ): Promise<VehicleResponseDto> {
    try {
      const response = await vehicleService.createVehicle(payload);
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      const errorCode = extractErrorCode(error);
      if (errorCode !== ErrorCode.VEHICLE_ALREADY_EXISTS) {
        await dispatch("notificator/apiError", error, { root: true });
      }
      throw error;
    }
  },

  async updateVehicle(
    { dispatch }: Ctx,
    payload: { dealerVehicleId: string; data: UpdateVehicleDto }
  ): Promise<VehicleResponseDto> {
    try {
      const response = await vehicleService.updateVehicle(
        payload.dealerVehicleId,
        payload.data
      );
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async deleteVehicle({ dispatch }: Ctx, dealerVehicleId: string) {
    try {
      await vehicleService.deleteVehicle(dealerVehicleId);
      dispatch("notificator/success", "delete-success", { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async uploadVehicleImage(
    { commit, dispatch }: Ctx,
    payload: { dealerVehicleId: string; file: File }
  ): Promise<VehicleResponseDto> {
    try {
      const vehicle = await vehicleService.uploadImage(payload.dealerVehicleId, payload.file);
      commit("setCurrentVehicle", vehicle);
      return vehicle;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async deleteVehicleImage(
    { commit, dispatch, state }: Ctx,
    payload: { dealerVehicleId: string; vehicleImageId: string }
  ) {
    try {
      await vehicleService.deleteImage(payload.dealerVehicleId, payload.vehicleImageId);
      if (state.currentVehicle) {
        commit("setCurrentVehicle", {
          ...state.currentVehicle,
          images: (state.currentVehicle.images || []).filter(
            (img) => img.vehicleImageId !== payload.vehicleImageId
          ),
        });
      }
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },
};

const getters = {
  vehicles: (state: State) => state.vehicles,
  total: (state: State) => state.total,
  page: (state: State) => state.page,
  pageSize: (state: State) => state.pageSize,
  totalPages: (state: State) => state.totalPages,
  currentVehicle: (state: State) => state.currentVehicle,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
